import type { AutomationRuleDto, AutomationTemplateDto } from '@fos/shared/contracts/automations';
import {
  AUTOMATION_RUN_STATUS_LABELS,
  AUTOMATION_RUN_STATUS_TONES,
  automationActionsText,
  automationTriggerText,
} from '@fos/shared/labels';
import { Check, Plus, Workflow } from 'lucide-react';
import { useEffect, useMemo, useState } from 'react';
import { useSearchParams } from 'react-router';
import { appErrorCode, errorMessage } from '../../../lib/api';
import { formatRelativePast } from '../../../lib/format';
import { trpc } from '../../../lib/trpc';
import { useAccess } from '../../../lib/useAccess';
import { Button } from '../../../ui/Button';
import { Checkbox } from '../../../ui/Checkbox';
import { ListRow } from '../../../ui/ListRow';
import { Section } from '../../../ui/Section';
import { EmptyState, ErrorState, ListSkeleton } from '../../../ui/States';
import { StatusPill } from '../../../ui/StatusPill';
import { toast } from '../../../ui/Toast';
import { TOUCH_BUTTON } from '../../mobile';
import { SettingsHeader } from '../pages/SettingsLayout';
import { RuleEditorDialog } from './RuleEditorDialog';
import { RuleSheet } from './RuleSheet';

/**
 * Ustawienia → Automatyzacje (Fala 10, DECISIONS 2026-10-07 [S] „Copilot ma być prawdziwym asystentem”; czeka na
 * projekt w Figmie): gotowe szablony włączane jednym kliknięciem, lista reguł (wyzwalacz, akcje, ostatnie
 * uruchomienie), karta reguły z historią i zgodą na „Zleć Claude”, edytor reguły. Spokojnie: białe sekcje, mało kolorów.
 * Adres `?regula=<id>` otwiera kartę reguły (link z maila i Inboxu).
 */
export function AutomationsSettingsPage() {
  const access = useAccess();
  const [params, setParams] = useSearchParams();
  const [showTeam, setShowTeam] = useState(false);
  const rules = trpc.automations.list.useQuery({ all: showTeam && access.isAdmin });
  const [editor, setEditor] = useState<{ open: boolean; rule: AutomationRuleDto | null }>({ open: false, rule: null });
  const openId = params.get('regula');
  const openRule = useMemo(() => rules.data?.find((r) => r.id === openId) ?? null, [rules.data, openId]);

  const setOpenId = (id: string | null) => {
    const next = new URLSearchParams(params);
    if (id) next.set('regula', id);
    else next.delete('regula');
    setParams(next, { replace: true });
  };

  // reguła spoza bieżącej listy (np. zespołu) — przełącz na widok zespołu dla admina
  useEffect(() => {
    if (openId && rules.data && !openRule && access.isAdmin && !showTeam) setShowTeam(true);
  }, [openId, rules.data, openRule, access.isAdmin, showTeam]);

  return (
    <div>
      <SettingsHeader
        title="Automatyzacje"
        description="Reguły: gdy coś się wydarzy, Fravity OS powiadomi Cię, utworzy zadanie albo zleci diagnozę Claude."
        actions={
          <Button
            variant="primary"
            icon={Plus}
            onClick={() => setEditor({ open: true, rule: null })}
            className={TOUCH_BUTTON}
          >
            Nowa reguła
          </Button>
        }
      />
      <TemplatesSection onOpenRule={setOpenId} />
      <Section
        className="mt-4 md:mt-6"
        title="Reguły"
        action={
          access.isAdmin ? (
            <Checkbox checked={showTeam} onCheckedChange={setShowTeam} label="Także reguły zespołu" />
          ) : undefined
        }
      >
        {rules.isPending ? (
          <ListSkeleton rows={3} />
        ) : rules.isError ? (
          <ErrorState compact onRetry={() => void rules.refetch()} retrying={rules.isFetching} />
        ) : rules.data.length === 0 ? (
          <EmptyState
            compact
            icon={Workflow}
            title="Brak reguł"
            description="Włącz gotowy szablon powyżej albo dodaj własną regułę."
          />
        ) : (
          <ul>
            {rules.data.map((r) => (
              <li key={r.id}>
                <ListRow
                  onClick={() => setOpenId(r.id)}
                  chevron
                  title={r.name}
                  description={`${automationTriggerText(r.trigger)} → ${automationActionsText(r.actions)}${
                    r.isMine ? '' : ` · ${r.owner.name}`
                  }`}
                  trailing={<RuleState rule={r} />}
                  className="border-b border-line"
                />
              </li>
            ))}
          </ul>
        )}
      </Section>
      <p className="mt-4 text-caption text-fg-muted">
        Akcje działają z Twoimi uprawnieniami. Maile do klientów i faktury nadal przechodzą przez Akceptacje; zlecenia
        dla Claude — tylko po zgodzie właściciela, z limitem na dobę.
      </p>

      <RuleEditorDialog
        open={editor.open}
        rule={editor.rule}
        onOpenChange={(open) => setEditor((e) => ({ ...e, open }))}
        onSaved={(rule) => setOpenId(rule.id)}
      />
      <RuleSheet
        rule={openRule}
        open={Boolean(openRule) && !editor.open}
        onOpenChange={(open) => {
          if (!open) setOpenId(null);
        }}
        onEdit={(rule) => setEditor({ open: true, rule })}
      />
    </div>
  );
}

function RuleState({ rule }: { rule: AutomationRuleDto }) {
  if (!rule.enabled) return <StatusPill tone="neutral">Wyłączona</StatusPill>;
  if (rule.claude.required && !rule.claude.allowed) return <StatusPill tone="warning">Claude: brak zgody</StatusPill>;
  if (rule.lastRun) {
    return (
      <span className="flex min-w-0 items-center gap-2">
        <StatusPill tone={AUTOMATION_RUN_STATUS_TONES[rule.lastRun.status]}>
          {AUTOMATION_RUN_STATUS_LABELS[rule.lastRun.status]}
        </StatusPill>
        <span className="hidden text-caption text-fg-muted md:inline">{formatRelativePast(rule.lastRun.at)}</span>
      </span>
    );
  }
  return <StatusPill tone="success">Włączona</StatusPill>;
}

function TemplatesSection({ onOpenRule }: { onOpenRule: (id: string) => void }) {
  const templates = trpc.automations.templates.useQuery({});
  const utils = trpc.useUtils();
  const enable = trpc.automations.enableTemplate.useMutation();
  const [busyKey, setBusyKey] = useState<string | null>(null);

  const turnOn = async (t: AutomationTemplateDto) => {
    setBusyKey(t.key);
    try {
      const rule = await enable.mutateAsync({ key: t.key });
      await utils.automations.invalidate();
      toast.success(
        'Szablon włączony',
        rule.claude.required && !rule.claude.allowed
          ? { description: 'Zlecanie Claude zadziała po zgodzie właściciela — otwórz regułę.' }
          : undefined,
      );
      if (rule.claude.required && !rule.claude.allowed) onOpenRule(rule.id);
    } catch (err) {
      if (appErrorCode(err) !== 'STEP_UP_REQUIRED') toast.error(errorMessage(err));
    } finally {
      setBusyKey(null);
    }
  };

  return (
    <Section className="mt-6" title="Gotowe szablony">
      {templates.isPending ? (
        <ListSkeleton rows={2} />
      ) : templates.isError ? (
        <ErrorState compact onRetry={() => void templates.refetch()} retrying={templates.isFetching} />
      ) : (
        <ul className="grid gap-3 md:grid-cols-2">
          {templates.data.map((t) => (
            <li key={t.key} className="flex flex-col gap-2 rounded-lg border border-line p-3.5">
              <p className="text-body-s font-medium text-fg">{t.title}</p>
              <p className="flex-1 text-caption text-fg-muted">{t.description}</p>
              <div>
                {t.ruleId ? (
                  <Button
                    size="s"
                    className={TOUCH_BUTTON}
                    variant="ghost"
                    icon={Check}
                    onClick={() => onOpenRule(t.ruleId as string)}
                  >
                    Włączony — pokaż
                  </Button>
                ) : (
                  <Button
                    size="s"
                    className={TOUCH_BUTTON}
                    variant="secondary"
                    loading={busyKey === t.key}
                    disabled={busyKey !== null}
                    onClick={() => void turnOn(t)}
                  >
                    Włącz
                  </Button>
                )}
              </div>
            </li>
          ))}
        </ul>
      )}
    </Section>
  );
}
